import type { TableProps, ColumnsType } from "antd/es/table";
import type { TableRowSelection } from "antd/es/table/interface";
import { Card, Checkbox, Pagination, Spin, Table, Typography } from "antd";
import {
  Component,
  Key,
  ReactNode,
  useMemo,
  useState,
  type ErrorInfo,
} from "react";
import { useMediaQuery } from "@mui/material";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { spacing, colors, borderRadius, fontSize } from "~/styles/tokens";
import i18n from "~/i18n";

const { Text } = Typography;

const DEFAULT_MOBILE_PAGE_SIZE = 10;

/**
 * Configuration for mobile card view
 */
export interface MobileCardConfig {
  /** Column key(s) shown in the card headline (first = title, rest = subtitles). */
  headlineKeys?: string[];
  /** Column key to use as the card title (defaults to first column) */
  titleKey?: string;
  /** Column key for subtitle (optional) */
  subtitleKey?: string;
  /** Column keys to always show (not in expandable section) */
  primaryKeys?: string[];
  /** Column key for actions (defaults to 'actions') */
  actionsKey?: string;
  /** Hide the expandable details section */
  hideDetails?: boolean;
}

export interface MobileDefaultSortConfig {
  /** Column dataIndex to sort by when rendering mobile cards */
  dataIndex: string | string[];
  /** Sort order to apply in mobile card view */
  order: "ascend" | "descend";
}

interface CustomTableProps {
  tableHeaders: ReactNode;
  tableProps: TableProps<any>;
  footer?: ReactNode;
  style?: React.CSSProperties;
  /** Configuration for mobile card view. Set to false to disable card view. */
  mobileCardConfig?: MobileCardConfig | false;
  /** Force mobile card view regardless of screen size. Useful for testing. */
  forceMobileView?: boolean;
  /** Default sort applied only to mobile card view. */
  mobileDefaultSort?: MobileDefaultSortConfig;
}

const getColumnKey = (column: any): string | undefined => {
  if (column.key != null && column.key !== "") {
    return String(column.key);
  }
  const dataIndex = column.dataIndex;
  if (Array.isArray(dataIndex)) {
    return dataIndex.join(".");
  }
  if (dataIndex != null && dataIndex !== "") {
    return String(dataIndex);
  }
  return undefined;
};

const findColumn = (columns: ColumnsType<any>, key: string) =>
  columns.find((col: any) => getColumnKey(col) === key);

const resolveHeadlineKeys = (
  config: MobileCardConfig,
  columns: ColumnsType<any>,
): string[] => {
  if (config.headlineKeys?.length) {
    return config.headlineKeys;
  }

  const keys: string[] = [];
  if (config.titleKey) {
    keys.push(config.titleKey);
  } else {
    const firstKey = getColumnKey(columns[0]);
    if (firstKey) {
      keys.push(firstKey);
    }
  }
  if (config.subtitleKey) {
    keys.push(config.subtitleKey);
  }
  return keys;
};

/**
 * Get nested value from object using dot notation or array path
 */
const getNestedValue = (obj: any, path: string | string[]): any => {
  const keys = Array.isArray(path) ? path : path.split(".");
  return keys.reduce((acc, key) => acc?.[key], obj);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  !("$$typeof" in (value as object));

/** Reject plain objects that would crash React; keep elements/primitives. */
const sanitizeCellOutput = (value: unknown): ReactNode => {
  if (isPlainObject(value)) {
    return "-";
  }
  if (Array.isArray(value) && value.some(isPlainObject)) {
    return "-";
  }
  return value as ReactNode;
};

/** Format a raw (no column.render) cell value for mobile cards. */
const formatRawCellValue = (value: unknown): ReactNode => {
  if (value == null || value === "") {
    return "-";
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (isPlainObject(value) || Array.isArray(value)) {
    return "-";
  }
  return value as ReactNode;
};

const updateRowSelection = (
  rowSelection: TableRowSelection<any>,
  rowKey: Key,
  record: any,
  checked: boolean,
  allRecords: readonly any[],
  getRowKeyForRecord: (record: any, index: number) => Key,
) => {
  const selectedRowKeys = rowSelection.selectedRowKeys ?? [];
  let newSelectedRowKeys: Key[];

  if (rowSelection.type === "radio") {
    newSelectedRowKeys = checked ? [rowKey] : [];
  } else if (checked) {
    newSelectedRowKeys = selectedRowKeys.includes(rowKey)
      ? selectedRowKeys
      : [...selectedRowKeys, rowKey];
  } else {
    newSelectedRowKeys = selectedRowKeys.filter((key) => key !== rowKey);
  }

  const selectedRows = allRecords.filter((item, index) =>
    newSelectedRowKeys.includes(getRowKeyForRecord(item, index)),
  );

  rowSelection.onChange?.(newSelectedRowKeys, selectedRows, {
    type: rowSelection.type === "radio" ? "single" : "multiple",
  });
  rowSelection.onSelect?.(record, checked, selectedRows, {} as Event);
};

class MobileCardErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Mobile card render failed:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card
          size="small"
          style={{
            marginBottom: spacing.inline,
            borderRadius: borderRadius.card,
          }}
          styles={{ body: { padding: spacing.component } }}
        >
          <Text type="secondary">
            {i18n.t("Unable_to_display_this_row")}
          </Text>
        </Card>
      );
    }
    return this.props.children;
  }
}

/**
 * Mobile Card Item Component
 */
const MobileCardItem = ({
  record,
  columns,
  config,
  rowKey,
  rowSelection,
  allRecords,
  getRowKeyForRecord,
}: {
  record: any;
  columns: ColumnsType<any>;
  config: MobileCardConfig;
  rowKey: string | number;
  rowSelection?: TableRowSelection<any>;
  allRecords?: readonly any[];
  getRowKeyForRecord?: (record: any, index: number) => Key;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isSelectable = Boolean(rowSelection);
  const selectedRowKeys = rowSelection?.selectedRowKeys ?? [];
  const isSelected = selectedRowKeys.includes(rowKey);
  const checkboxProps = rowSelection?.getCheckboxProps?.(record) ?? {};
  const isSelectionDisabled = checkboxProps.disabled ?? false;

  const headlineKeys = resolveHeadlineKeys(config, columns);
  const headlineColumns = headlineKeys
    .map((key) => findColumn(columns, key))
    .filter(Boolean);

  const actionsColumn = findColumn(columns, config.actionsKey || "actions");

  const primaryKeys = config.primaryKeys || [];
  const primaryColumns = primaryKeys
    .map((key) => findColumn(columns, key))
    .filter(Boolean);

  const excludeKeys = new Set<string>([
    ...headlineKeys,
    ...primaryKeys,
    config.actionsKey || "actions",
  ]);
  const detailColumns = columns.filter((col: any) => {
    const columnKey = getColumnKey(col);
    return columnKey != null && !excludeKeys.has(columnKey);
  });

  const renderColumnValue = (column: any, record: any): ReactNode => {
    const dataIndex = column.dataIndex;
    const value = dataIndex ? getNestedValue(record, dataIndex) : record;

    if (column.render) {
      try {
        return sanitizeCellOutput(column.render(value, record, 0));
      } catch (error) {
        console.error("Mobile card column render failed:", error);
        return "-";
      }
    }
    return formatRawCellValue(value);
  };

  const actionsValue = actionsColumn
    ? renderColumnValue(actionsColumn, record)
    : null;

  const hasDetails = detailColumns.length > 0 && !config.hideDetails;

  const handleSelectionChange = (checked: boolean) => {
    if (!rowSelection || !allRecords || !getRowKeyForRecord) {
      return;
    }

    updateRowSelection(
      rowSelection,
      rowKey,
      record,
      checked,
      allRecords,
      getRowKeyForRecord,
    );
  };

  return (
    <Card
      size="small"
      style={{
        marginBottom: spacing.inline,
        borderRadius: borderRadius.card,
        ...(isSelectable && isSelected
          ? {
              borderColor: colors.actionPrimary,
              backgroundColor: "rgba(24, 144, 255, 0.04)",
            }
          : {}),
      }}
      styles={{ body: { padding: spacing.component } }}
    >
      {/* Header: Selection + Headline + Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: spacing.inline,
        }}
      >
        {isSelectable && (
          <Checkbox
            {...checkboxProps}
            checked={isSelected}
            disabled={isSelectionDisabled}
            onChange={(event) => handleSelectionChange(event.target.checked)}
            onClick={(event) => event.stopPropagation()}
            style={{ marginTop: 2, flexShrink: 0 }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {headlineColumns.map((column: any, idx) =>
            idx === 0 ? (
              <div
                key={getColumnKey(column)}
                style={{
                  fontSize: fontSize.body,
                  fontWeight: 600,
                  display: "block",
                }}
              >
                {renderColumnValue(column, record)}
              </div>
            ) : (
              <div
                key={getColumnKey(column)}
                style={{
                  fontSize: fontSize.caption,
                  color: colors.textSecondary,
                }}
              >
                {renderColumnValue(column, record)}
              </div>
            ),
          )}
        </div>
        {actionsValue && <div style={{ flexShrink: 0 }}>{actionsValue}</div>}
      </div>

      {/* Primary Fields (always visible) */}
      {primaryColumns.length > 0 && (
        <div style={{ marginTop: spacing.inline }}>
          {primaryColumns.map((column: any, idx) => (
            <div
              key={column.key || column.dataIndex || idx}
              style={{
                display: "flex",
                gap: spacing.inline,
                fontSize: fontSize.caption,
                marginTop: idx > 0 ? "4px" : 0,
              }}
            >
              <Text type="secondary" style={{ minWidth: 80 }}>
                {column.title}:
              </Text>
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderColumnValue(column, record)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Expandable Details */}
      {hasDetails && (
        <div style={{ marginTop: spacing.inline }}>
          <div
            onClick={() => setExpanded(!expanded)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
              color: colors.actionPrimary,
              fontSize: fontSize.caption,
              padding: "4px 0",
            }}
          >
            {expanded ? (
              <DownOutlined style={{ fontSize: "10px" }} />
            ) : (
              <RightOutlined style={{ fontSize: "10px" }} />
            )}
            <span>{expanded ? "Hide details" : "Show details"}</span>
          </div>

          {expanded && (
            <div
              style={{
                marginTop: spacing.inline,
                paddingTop: spacing.inline,
                borderTop: `1px solid ${colors.borderSubtle}`,
              }}
            >
              {detailColumns.map((column: any, idx) => (
                <div
                  key={column.key || column.dataIndex || idx}
                  style={{
                    display: "flex",
                    gap: spacing.inline,
                    fontSize: fontSize.caption,
                    marginTop: idx > 0 ? "4px" : 0,
                  }}
                >
                  <Text
                    type="secondary"
                    style={{ minWidth: 100, flexShrink: 0 }}
                  >
                    {column.title}:
                  </Text>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {renderColumnValue(column, record)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const resolveMobilePagination = (
  pagination: TableProps<any>["pagination"],
  total: number,
) => {
  if (pagination === false) {
    return null;
  }

  const config =
    pagination && typeof pagination === "object" ? pagination : {};
  const pageSize =
    typeof config.pageSize === "number" && config.pageSize > 0
      ? config.pageSize
      : DEFAULT_MOBILE_PAGE_SIZE;

  return {
    pageSize,
    total,
    showSizeChanger: false,
  };
};

/**
 * CustomTable - Responsive table component
 *
 * Automatically switches between:
 * - Desktop: Traditional table view with horizontal scroll
 * - Mobile: Card list view with expandable details
 */
export const CustomTable = ({
  tableHeaders,
  tableProps,
  footer,
  style,
  mobileCardConfig,
  forceMobileView = false,
  mobileDefaultSort,
}: CustomTableProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [mobilePage, setMobilePage] = useState(1);

  const { dataSource, columns, loading, pagination, rowKey, rowSelection } =
    tableProps;

  // Determine if we should show mobile card view
  // Can be forced via prop (for testing) or triggered by screen size
  const showMobileCards =
    (forceMobileView || isMobile) &&
    mobileCardConfig !== false &&
    columns &&
    columns.length > 0;

  // Default mobile config
  const config: MobileCardConfig = {
    actionsKey: "actions",
    ...mobileCardConfig,
  };

  // Get row key for each record
  const getRowKey = (record: any, index: number): string | number => {
    if (typeof rowKey === "function") {
      const key = rowKey(record);
      return typeof key === "bigint" ? String(key) : key ?? index;
    }
    if (typeof rowKey === "string") {
      return record[rowKey] ?? index;
    }
    return record.key ?? record.id ?? index;
  };

  const mobileDataSource = useMemo(() => {
    if (!dataSource || !mobileDefaultSort) {
      return dataSource;
    }

    const sorted = [...dataSource];
    const factor = mobileDefaultSort.order === "ascend" ? 1 : -1;

    sorted.sort((a: any, b: any) => {
      const valueA = getNestedValue(a, mobileDefaultSort.dataIndex);
      const valueB = getNestedValue(b, mobileDefaultSort.dataIndex);

      if (typeof valueA === "number" && typeof valueB === "number") {
        return (valueA - valueB) * factor;
      }

      return (
        String(valueA ?? "").localeCompare(String(valueB ?? ""), undefined, {
          sensitivity: "base",
          numeric: true,
        }) * factor
      );
    });

    return sorted;
  }, [dataSource, mobileDefaultSort]);

  const mobilePagination = resolveMobilePagination(
    pagination,
    mobileDataSource?.length ?? 0,
  );

  const pagedMobileDataSource = useMemo(() => {
    if (!mobileDataSource) {
      return mobileDataSource;
    }
    if (!mobilePagination) {
      return mobileDataSource;
    }

    const pageSize = mobilePagination.pageSize;
    const totalPages = Math.max(
      1,
      Math.ceil(mobileDataSource.length / pageSize),
    );
    const currentPage = Math.min(Math.max(mobilePage, 1), totalPages);
    const start = (currentPage - 1) * pageSize;
    return mobileDataSource.slice(start, start + pageSize);
  }, [mobileDataSource, mobilePagination, mobilePage]);

  const mobileTotalPages = mobilePagination
    ? Math.max(
        1,
        Math.ceil((mobileDataSource?.length ?? 0) / mobilePagination.pageSize),
      )
    : 1;
  const clampedMobilePage = Math.min(Math.max(mobilePage, 1), mobileTotalPages);

  return (
    <Card
      styles={{
        body: {
          display: "block",
          overflow: showMobileCards ? "visible" : "auto",
          padding: showMobileCards ? spacing.component : undefined,
        },
      }}
      style={style}
    >
      <div
        style={{
          marginBottom: showMobileCards ? spacing.component : "2rem",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {tableHeaders}
      </div>

      {showMobileCards ? (
        // Mobile: Card List View
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: spacing.page }}>
              <Spin />
            </div>
          ) : !mobileDataSource || mobileDataSource.length === 0 ? (
            <div style={{ textAlign: "center", padding: spacing.page }}>
              <Text type="secondary">No data</Text>
            </div>
          ) : (
            <>
              {(pagedMobileDataSource ?? []).map((record, index) => {
                const pageOffset = mobilePagination
                  ? (clampedMobilePage - 1) * mobilePagination.pageSize
                  : 0;
                const key = getRowKey(record, pageOffset + index);
                return (
                  <MobileCardErrorBoundary key={key}>
                    <MobileCardItem
                      record={record}
                      columns={columns as ColumnsType<any>}
                      config={config}
                      rowKey={key}
                      rowSelection={rowSelection}
                      allRecords={dataSource ?? []}
                      getRowKeyForRecord={getRowKey}
                    />
                  </MobileCardErrorBoundary>
                );
              })}
              {mobilePagination && mobileDataSource.length > mobilePagination.pageSize && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: spacing.component,
                  }}
                >
                  <Pagination
                    size="small"
                    current={clampedMobilePage}
                    pageSize={mobilePagination.pageSize}
                    total={mobilePagination.total}
                    showSizeChanger={false}
                    onChange={(page) => setMobilePage(page)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        // Desktop: Traditional Table View
        <Table bordered scroll={{ x: "max-content" }} {...tableProps} />
      )}

      {footer}
    </Card>
  );
};
